import { UserRole } from '../../lib/contracts';

export const APP_ROLES = {
  customer: 'customer',
  driver: 'driver',
  in_store_shopper: 'in_store_shopper',
  admin: 'admin',
  owner: 'owner',
  affiliate: 'affiliate'
} as const;

export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

const FRONTEND_TO_BACKEND_ROLE: Record<UserRole, AppRole> = {
  [UserRole.CUSTOMER]: APP_ROLES.customer,
  [UserRole.DRIVER]: APP_ROLES.driver,
  [UserRole.IN_STORE_SHOPPER]: APP_ROLES.in_store_shopper,
  [UserRole.ADMIN]: APP_ROLES.admin,
  [UserRole.OWNER]: APP_ROLES.owner,
  [UserRole.AFFILIATE]: APP_ROLES.affiliate
};

const BACKEND_TO_FRONTEND_ROLE = Object.fromEntries(
  Object.entries(FRONTEND_TO_BACKEND_ROLE).map(([frontendRole, backendRole]) => [backendRole, frontendRole])
) as Record<AppRole, UserRole>;

export function normalizeRole(role: unknown): AppRole | null {
  if (!role) return null;

  if (typeof role === 'string') {
    const raw = role.trim().toLowerCase();
    if (raw in APP_ROLES) {
      return raw as AppRole;
    }
  }

  return FRONTEND_TO_BACKEND_ROLE[role as UserRole] ?? null;
}

export function toUserRole(role: unknown): UserRole | null {
  const normalizedRole = normalizeRole(role);
  return normalizedRole ? BACKEND_TO_FRONTEND_ROLE[normalizedRole] : null;
}

export function normalizeRoles(roles: unknown): AppRole[] {
  if (Array.isArray(roles)) {
    return Array.from(
      new Set(roles.map((r) => normalizeRole(r)).filter((r): r is AppRole => r !== null))
    );
  }
  const single = normalizeRole(roles);
  return single ? [single] : [];
}

export function hasAnyRole(roleOrUser: unknown, allowedRoles: readonly AppRole[]): boolean {
  let candidate: unknown = roleOrUser;
  if (roleOrUser && typeof roleOrUser === 'object' && !Array.isArray(roleOrUser)) {
    const userObj = roleOrUser as { roles?: unknown; role?: unknown };
    candidate = Array.isArray(userObj.roles) && userObj.roles.length > 0
      ? userObj.roles
      : (userObj.role ? [userObj.role] : []);
  }

  const normalizedUserRoles = normalizeRoles(candidate);
  if (normalizedUserRoles.length === 0) return false;

  // Owner has root access.
  if (normalizedUserRoles.includes(APP_ROLES.owner)) return true;

  const normalizedAllowedRoles = Array.from(
    new Set(
      allowedRoles
        .map((candidateRole) => normalizeRole(candidateRole))
        .filter((candidateRole): candidateRole is AppRole => candidateRole !== null)
    )
  );

  return normalizedUserRoles.some((r) => normalizedAllowedRoles.includes(r));
}

export const hasRole = hasAnyRole;

